import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { ApiError } from '@/lib/api'
import {
  compareOf,
  impactStripFor,
  tabsOf,
  type CompareBase,
} from '../SnippetEditorSheet/model'
import {
  CONTENT_VIEW,
  resolveView,
  type SheetView,
} from '../SnippetEditorSheet/view'
import { hidesFromOtherOrgs, type SnippetDraft } from '../drafts'
import type { SnippetImpact } from '../types'
import {
  isCopyExistsError,
  isDuplicateNameError,
  isStaleWriteError,
  plural,
  snippetErrorMessage,
} from '../utils'
import { useSnippetImpactMutation } from './useSnippetImpact'
import {
  useDeleteSnippetVariantMutation,
  usePutSnippetVariantMutation,
  useSnippetVariantsQuery,
} from './useSnippetVariants'
import {
  useCreateSnippetMutation,
  useCustomizeSnippetMutation,
  useDeleteSnippetMutation,
  useInvalidateSnippets,
  useSnippetQuery,
  useSnippetUsageQuery,
  useUpdateSnippetMutation,
} from './useSnippets'

const DUPLICATE_NAME_MESSAGE = 'That name is already taken.'
const RELOAD_MESSAGE =
  'This snippet loaded without a version. Reopen it before saving.'
const STALE_WRITE_MESSAGE =
  'Someone else saved this snippet while you were editing. Reopen it and reapply your change.'
const STALE_VARIANT_MESSAGE =
  'Someone else changed this variant while you were editing. Reopen it and reapply your change.'
export const DELETED_MESSAGE = 'This snippet was deleted.'
export const COPY_RESET_ELSEWHERE_MESSAGE =
  'Someone in your organization reset this snippet to Careforce.'
export const ALREADY_CUSTOMIZED_MESSAGE =
  'Someone in your organization already customized this snippet. Reopen it to see their copy.'
const IMPACT_GATE_FAILED_MESSAGE = 'Could not check impact. Try again.'

function duplicateCopyNameMessage(name: string): string {
  return `You already have a snippet named “${name}”. Rename your copy.`
}

/**
 * Draft state and save handling for `SnippetEditorSheet`, shared by the library
 * page and by the sheet the script editor opens from a snippet block.
 *
 * Four kinds of draft pass through here: an org's own snippet (or a new one),
 * one duty's variant of it, a read-only Careforce snippet, and the org's copy
 * of a Careforce snippet being created (`customize`) or edited (`copy`).
 */
export function useSnippetEditorSheet(
  onClosed?: () => void,
  initialDraft: SnippetDraft | null = null
) {
  const create = useCreateSnippetMutation()
  const update = useUpdateSnippetMutation()
  const customize = useCustomizeSnippetMutation()
  const remove = useDeleteSnippetMutation()
  const checkImpact = useSnippetImpactMutation()
  const putVariant = usePutSnippetVariantMutation()
  const resetVariant = useDeleteSnippetVariantMutation()
  const invalidateSnippets = useInvalidateSnippets()

  const [draft, setDraft] = useState<SnippetDraft | null>(initialDraft)
  // What the draft opened as. Dirty means the user's edits differ from it.
  const [baseline, setBaseline] = useState<SnippetDraft | null>(initialDraft)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)
  // The body the check ran against, kept with its result. A report describes
  // one body, so freshness is whether that body is still on screen. A separate
  // stale flag misses edits made mid-request, when there is no result to mark.
  const [checked, setChecked] = useState<{
    content: string
    impact: SnippetImpact
  } | null>(null)
  const [impactError, setImpactError] = useState<string | null>(null)
  // A shared save that the pre-save check found failures for, awaiting the
  // author's "Save anyway".
  const [confirmingImpact, setConfirmingImpact] =
    useState<SnippetImpact | null>(null)
  const [view, setView] = useState<SheetView>(CONTENT_VIEW)
  // Keys the editor, which reads its body only at mount. Bumped on every open
  // (a reopen during the sheet's close animation reuses the mounted editor,
  // and new drafts all have a null id) and whenever the body is replaced.
  const [editorKey, setEditorKey] = useState(0)
  const isSaving =
    create.isPending ||
    update.isPending ||
    customize.isPending ||
    putVariant.isPending ||
    checkImpact.isPending
  // Set synchronously in save(); `isSaving` is the state of the render each
  // handler closed over, so two clicks in one tick both read it as false.
  const savingRef = useRef(false)

  const impact =
    checked && checked.content === draft?.content ? checked.impact : null
  const isImpactStale = checked !== null && checked.content !== draft?.content

  // Only where the Variants tab shows: fetching elsewhere is work nobody sees.
  const variantsQuery = useSnippetVariantsQuery(
    draft?.id ?? null,
    tabsOf(draft).includes('variants')
  )
  const activeView = resolveView(view, tabsOf(draft))
  const comparing = activeView.tab === 'content' && activeView.compare
  const compare = compareOf(draft)
  // The Careforce text this copy diverged from. Only fetched once the author
  // opens the comparison.
  const sourceQuery = useSnippetQuery(
    draft?.copy?.sourceId ?? null,
    comparing && compare?.base === 'source'
  )
  const compareBases: Record<
    CompareBase,
    { content: string | null; isLoading: boolean }
  > = {
    source: {
      content: sourceQuery.data?.content ?? null,
      isLoading: sourceQuery.isPending && sourceQuery.isFetching,
    },
    opened: { content: baseline?.content ?? null, isLoading: false },
    variantShared: {
      content: draft?.variant?.sharedContent ?? null,
      isLoading: false,
    },
  }
  const compareBase = compare && compareBases[compare.base]

  // From the baseline: a row only just switched to shared has no readers yet.
  const listsReaders = tabsOf(baseline).includes('orgs')
  const usageQuery = useSnippetUsageQuery(
    draft?.id ?? null,
    listsReaders && activeView.tab === 'orgs'
  )

  const openDraft = (next: SnippetDraft) => {
    setNameError(null)
    setChecked(null)
    setImpactError(null)
    setConfirmingDiscard(false)
    setConfirmingImpact(null)
    setView(CONTENT_VIEW)
    setDraft(next)
    setBaseline(next)
    setEditorKey((count) => count + 1)
  }

  const sharedChanged =
    (draft?.isShared ?? false) !== (baseline?.isShared ?? false)
  const hiddenChanged =
    hidesFromOtherOrgs(draft) !== hidesFromOtherOrgs(baseline)

  const isDirty =
    draft !== null &&
    baseline !== null &&
    (draft.name !== baseline.name ||
      draft.content !== baseline.content ||
      sharedChanged ||
      hiddenChanged)

  const runImpactCheck = async () => {
    if (!draft?.id) return null
    const content = draft.content
    setChecked(null)
    setImpactError(null)
    try {
      const result = await checkImpact.mutateAsync({ id: draft.id, content })
      setChecked({ content, impact: result })
      return result
    } catch (error) {
      setImpactError(snippetErrorMessage(error, 'Impact check failed'))
      return null
    }
  }

  const close = () => {
    setConfirmingDiscard(false)
    setConfirmingImpact(null)
    setDraft(null)
    onClosed?.()
  }

  // A 404 means the row is gone; the sheet has nothing left to save onto. For
  // the org's copy that means someone else reset it to Careforce.
  const closeIfDeleted = (error: unknown) => {
    if (!(error instanceof ApiError) || error.status !== 404) return false
    toast.error(draft?.copy ? COPY_RESET_ELSEWHERE_MESSAGE : DELETED_MESSAGE)
    void invalidateSnippets(draft?.variant?.dutyId)
    close()
    return true
  }

  const saveVariant = async (variant: NonNullable<SnippetDraft['variant']>) => {
    if (!draft?.id) return
    // KD16: the fork is client-side until the wording actually differs.
    // Creating the row on "Create variant" leaves an orphan behind every
    // abandoned edit, and freezes the duty out of shared updates while the
    // Reset button that would undo it is still hidden.
    if (variant.updatedAt === null && draft.content === variant.sharedContent) {
      close()
      return
    }
    await putVariant.mutateAsync({
      snippetId: draft.id,
      dutyId: variant.dutyId,
      content: draft.content,
      ...(variant.updatedAt ? { if_unmodified_since: variant.updatedAt } : {}),
    })
    toast.success('Variant saved')
    close()
  }

  const saveCopy = async (
    customizing: NonNullable<SnippetDraft['customize']>
  ) => {
    if (!draft || !baseline) return
    // The same rule as a variant: no copy until the author commits to one.
    // Close-without-change keeps the org following Careforce.
    if (draft.content === baseline.content && draft.name === baseline.name) {
      close()
      return
    }
    const name = draft.name.trim()
    try {
      await customize.mutateAsync({
        id: customizing.sourceId,
        content: draft.content,
        ...(name && name !== baseline.name ? { name } : {}),
      })
      toast.success('Saved as your copy', {
        description: `Your organization no longer follows Careforce updates for “${name || baseline.name}”.`,
      })
      close()
    } catch (error) {
      if (isCopyExistsError(error)) {
        toast.error(ALREADY_CUSTOMIZED_MESSAGE)
        void invalidateSnippets()
        close()
        return
      }
      if (isDuplicateNameError(error)) {
        setNameError(duplicateCopyNameMessage(name || baseline.name))
        return
      }
      if (closeIfDeleted(error)) return
      toast.error(snippetErrorMessage(error, 'Could not save your copy'))
    }
  }

  const runReset = async () => {
    if (!draft?.id || !draft.variant) return
    try {
      await resetVariant.mutateAsync({
        snippetId: draft.id,
        dutyId: draft.variant.dutyId,
      })
      toast.success('Reset to the primary snippet')
      close()
    } catch (error) {
      toast.error(snippetErrorMessage(error, 'Reset failed'))
    }
  }

  const runResetToCareforce = async () => {
    if (!draft?.id || !draft.copy) return
    try {
      await remove.mutateAsync(draft.id)
      const count = draft.instanceCount
      toast.success('Reset to Careforce', {
        description:
          count > 0
            ? `Careforce's text is back for ${plural(count, 'duty')}.`
            : "New scripts follow Careforce's text.",
      })
      close()
    } catch (error) {
      if (closeIfDeleted(error)) return
      toast.error(snippetErrorMessage(error, 'Reset failed'))
    }
  }

  const save = async ({ skipImpactGate = false } = {}) => {
    if (!draft || savingRef.current || draft.readOnly) return
    savingRef.current = true
    try {
      if (draft.variant) {
        try {
          await saveVariant(draft.variant)
        } catch (error) {
          if (isStaleWriteError(error)) {
            // Kept open: the body is the only copy of the user's work.
            toast.error(STALE_VARIANT_MESSAGE)
            return
          }
          if (closeIfDeleted(error)) return
          toast.error(snippetErrorMessage(error, 'Variant could not be saved'))
        }
        return
      }
      if (draft.customize) {
        await saveCopy(draft.customize)
        return
      }
      const name = draft.name.trim()
      if (!name) return

      try {
        if (draft.id === null) {
          await create.mutateAsync({ name, content: draft.content })
          toast.success('Snippet created')
        } else {
          if (!draft.updatedAt) {
            // Saving without it silently downgrades to last-write-wins on a
            // snippet several people can edit.
            toast.error(RELOAD_MESSAGE)
            return
          }
          const contentChanged = draft.content !== baseline?.content
          // A shared edit reaches every org's next call, so the advisory
          // check runs before the save and any failure asks first.
          if (
            contentChanged &&
            (draft.isShared || baseline?.isShared) &&
            !skipImpactGate
          ) {
            const result = await runImpactCheck()
            if (!result) {
              toast.error(IMPACT_GATE_FAILED_MESSAGE)
              return
            }
            if (result.failing_count > 0) {
              setConfirmingImpact(result)
              return
            }
          }
          await update.mutateAsync({
            id: draft.id,
            name,
            content: draft.content,
            ...(sharedChanged ? { is_shared: draft.isShared ?? false } : {}),
            ...(hiddenChanged ? { is_hidden: hidesFromOtherOrgs(draft) } : {}),
            if_unmodified_since: draft.updatedAt,
          })
          toast.success('Snippet saved')
        }
        close()
      } catch (error) {
        if (isStaleWriteError(error)) {
          // Kept open on purpose: the body is the only copy of the user's work.
          toast.error(STALE_WRITE_MESSAGE)
          return
        }
        if (isDuplicateNameError(error)) {
          setNameError(DUPLICATE_NAME_MESSAGE)
          return
        }
        if (closeIfDeleted(error)) return
        toast.error(snippetErrorMessage(error, 'Snippet could not be saved'))
      }
    } finally {
      savingRef.current = false
    }
  }

  return {
    openDraft,
    sheetProps: {
      draft,
      editorKey,
      view: activeView,
      onViewChange: setView,
      compareBase,
      onUseBase: () => {
        const base = compareBase?.content
        if (base == null || !compare) return
        setImpactError(null)
        setDraft((current) => current && { ...current, content: base })
        // The editor reads its body only at mount.
        setEditorKey((count) => count + 1)
        setView(CONTENT_VIEW)
        toast.success(compare.used)
      },
      nameError,
      isSaving,
      isDirty,
      impactStrip: impactStripFor({
        impact,
        isChecking: checkImpact.isPending,
        isStale: isImpactStale,
        error: impactError,
      }),
      confirmingImpact,
      onSaveAnyway: () => {
        setConfirmingImpact(null)
        void save({ skipImpactGate: true })
      },
      onCancelImpact: () => setConfirmingImpact(null),
      variants: variantsQuery.data?.variants ?? null,
      isLoadingVariants: variantsQuery.isPending && variantsQuery.isFetching,
      variantsError: variantsQuery.isError
        ? snippetErrorMessage(variantsQuery.error, 'Failed to load variants')
        : null,
      usage: listsReaders ? (usageQuery.data?.orgs ?? null) : [],
      usageError:
        listsReaders && usageQuery.isError
          ? snippetErrorMessage(usageQuery.error, 'Could not load usage.')
          : null,
      onRetryUsage: () => void usageQuery.refetch(),
      isResetting: resetVariant.isPending || remove.isPending,
      onResetVariant: () => void runReset(),
      onResetToCareforce: () => void runResetToCareforce(),
      onRetryVariants: () => void variantsQuery.refetch(),
      onCheckImpact: () => void runImpactCheck(),
      onNameChange: (name: string) => {
        setNameError(null)
        setDraft((current) => current && { ...current, name })
      },
      onContentChange: (content: string) => {
        setImpactError(null)
        setDraft((current) => current && { ...current, content })
      },
      onSharedChange: (isShared: boolean) =>
        setDraft((current) => current && { ...current, isShared }),
      onHiddenChange: (isHidden: boolean) =>
        setDraft((current) => current && { ...current, isHidden }),
      onSave: () => void save(),
      onOpenChange: (open: boolean) => {
        // Lock the sheet open while a write is in flight, so its result — a
        // 409 duplicate name in particular — can't be dismissed out from under
        // the user by Escape or an outside click.
        if (isSaving || resetVariant.isPending || remove.isPending) return
        if (!open) {
          if (isDirty) {
            setConfirmingDiscard(true)
            return
          }
          close()
        }
      },
      isConfirmingDiscard: confirmingDiscard,
      onDiscardChanges: close,
      onKeepEditing: () => setConfirmingDiscard(false),
    },
  }
}
